import RestartAlt from '@mui/icons-material/RestartAlt';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useState } from 'react';

import CustomButton from '../../designSystem/Button';
import { LightTooltip } from '../../designSystem/WhiteTooltip';

export interface StepData {
  label?: string;
  description?: string;
  content?: React.ReactNode;
  optional?: React.ReactNode;
  completed?: boolean;
  disabled?: boolean;
  async?: boolean;
  onAsyncStep?: () => Promise<void>;
  error?: boolean;
  errorMessage?: string;
}

export interface CustomStepperProps {
  steps: StepData[];
  activeStep: number;
  orientation?: 'horizontal' | 'vertical';
  showStepContent?: boolean;
  showNavigationButtons?: boolean;
  showResetButton?: boolean;
  completionMessage?: string;
  onStepChange?: (step: number) => void;
  onComplete?: () => void;
  onReset?: () => void;
  maxWidth?: number | string;
  sx?: object;
}

export default function CustomStepper({
  steps,
  activeStep,
  orientation = 'vertical',
  showStepContent = true,
  showNavigationButtons = true,
  showResetButton = true,
  completionMessage = '¡Todos los pasos completados!',
  onStepChange,
  onComplete,
  onReset,
  maxWidth = 400,
  sx = {},
}: CustomStepperProps) {
  const [asyncLoading, setAsyncLoading] = useState(false);

  const handleNext = async () => {
    const currentStep = steps[activeStep];

    // Si el paso actual es asíncrono, ejecutar la función async
    if (currentStep?.async && currentStep?.onAsyncStep) {
      setAsyncLoading(true);
      try {
        await currentStep.onAsyncStep();
        const nextStep = activeStep + 1;
        if (onStepChange) {
          onStepChange(nextStep);
        }
        if (nextStep === steps.length && onComplete) {
          onComplete();
        }
      } catch (error) {
        console.error('Error en paso asíncrono:', error);
      } finally {
        setAsyncLoading(false);
      }
    } else {
      // Comportamiento normal para pasos síncronos
      const nextStep = activeStep + 1;
      if (onStepChange) {
        onStepChange(nextStep);
      }
      if (nextStep === steps.length && onComplete) {
        onComplete();
      }
    }
  };

  const handleBack = () => {
    const prevStep = activeStep - 1;
    if (onStepChange) {
      onStepChange(prevStep);
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else if (onStepChange) {
      onStepChange(0);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (onStepChange && stepIndex <= activeStep) {
      onStepChange(stepIndex);
    }
  };

  const isStepCompleted = (stepIndex: number) => {
    return steps[stepIndex]?.completed || stepIndex < activeStep;
  };

  const isStepDisabled = (stepIndex: number) => {
    return steps[stepIndex]?.disabled || stepIndex > activeStep || asyncLoading;
  };

  return (
    <Box sx={{ maxWidth, ...sx }}>
      <Stepper
        activeStep={activeStep}
        orientation={orientation}
        sx={{ width: '100%' }}
      >
        {steps.map((step, index) => (
          <Step
            key={step.label ?? index}
            completed={isStepCompleted(index)}
            disabled={isStepDisabled(index)}
          >
            <StepLabel
              optional={
                step.error && step.errorMessage ? (
                  <Typography variant="caption" color="error">
                    {step.errorMessage}
                  </Typography>
                ) : (
                  step.optional
                )
              }
              error={step.error}
              onClick={() => handleStepClick(index)}
              sx={{
                cursor: index <= activeStep ? 'pointer' : 'default',
                '&:hover': index <= activeStep ? { opacity: 0.7 } : {},
                ...(step.label == null && {
                  '& .MuiStepLabel-label': { display: 'none' },
                }),
              }}
            >
              {step.label}
            </StepLabel>
            {showStepContent && (
              <StepContent>
                {step.content || <Typography>{step.description}</Typography>}
                {showNavigationButtons && (
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <CustomButton
                      color="primary"
                      onClick={handleNext}
                      style={{ marginTop: '8px', marginRight: '8px' }}
                      disabled={steps[index]?.disabled || asyncLoading}
                      loading={asyncLoading && index === activeStep}
                    >
                      {index === steps.length - 1 ? 'Finalizar' : 'Continuar'}
                    </CustomButton>
                    <CustomButton
                      color="secondary"
                      disabled={index === 0 || asyncLoading}
                      onClick={handleBack}
                      style={{ marginTop: '8px', marginRight: '8px' }}
                    >
                      Atrás
                    </CustomButton>
                  </Box>
                )}
              </StepContent>
            )}
          </Step>
        ))}
      </Stepper>

      {activeStep === steps.length && (
        <Paper square elevation={0} sx={{ p: 3, mt: 2 }}>
          <Alert
            variant="outlined"
            severity="success"
            sx={{ mb: 2 }}
            action={
              showResetButton ? (
                <LightTooltip
                  title={<Typography padding={1}>Reiniciar proceso</Typography>}
                >
                  <IconButton
                    onClick={handleReset}
                    size="small"
                    sx={{
                      marginLeft: '8px',
                      color: 'success.main',
                      '&:hover': {
                        backgroundColor: 'success.light',
                        color: 'success.dark',
                      },
                    }}
                  >
                    <RestartAlt fontSize="small" />
                  </IconButton>
                </LightTooltip>
              ) : null
            }
          >
            {completionMessage}
          </Alert>
        </Paper>
      )}
    </Box>
  );
}
